import { useTranslation } from "react-i18next";
import Card from "../shared/cards/Card";
import { swatch, downloadIcon } from "../../assets/components/svgs";
import { find, isEmpty } from "lodash";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import PickList from "../shared/Fields/PickList";
import CollapsibleCard from "./CollapsibleCard";
import Dropzone from "../shared/DragAndDrop/Dropzone";
import { setToastData } from "../../app/toastReducer";
import { IconButton } from "../primitives/buttons/IconButton";
import BiButton from "../primitives/buttons/BiButton.primitive";
import {
  findAssetFormat,
  getFileFormat,
  getFileName,
  getGlobalBucketName,
  customizationOptions,
} from "./Util";
import MenuButtonsPortal from "../Menu/MenuButtonsPortal";
import LoaderContainer from "../shared/loaderContainer/LoaderContainer";
import { AppConstants } from "../../constants/AppConstants";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  createSignedURL,
  uploadFile,
} from "../../http/asset-management-services";
import { HTTP_STATUS } from "../../http/constants/http.status";
import splashImg from "../../assets/default/splash.png";
import bannerImg from "../../assets/default/solution.svg";
import { setLocalAssets } from "../../app/appInitializeReducer";
import { setFavIcon } from "../../util/appInitializeUtil";
import { useLocalization } from "../../hooks/useLocalization";

interface AssetsProps {
  // save: Function;
  // disabled: boolean;
}

/**
 * @parentId customization
 * @manager Assets
 * @overview Manage the Portal's Logo and Images.
 * <section>
 *   <p>
 *     The first tab under Customization is Assets, enabling the management of the logos and images of the portal. It provides provisions for the following:
 *   </p>
 *   <table>
 *     <tr>
 *       <th>Name</th>
 *       <th>Type</th>
 *       <th>Description</th>
 *       <th>Format</th>
 *     </tr>
 *     <tr>
 *       <td>Logo Icon</td>
 *       <td>Icon file</td>
 *       <td>Used in the Favicon of the portal or at the browser tab.</td>
 *       <td>.ico</td>
 *     </tr>
 *     <tr>
 *       <td>Banner Image</td>
 *       <td>Image File</td>
 *       <td>Image displayed at the top left of the portal. Used for displaying Brand Logo.</td>
 *       <td>.png</td>
 *     </tr>
 *     <tr>
 *       <td>Splash Image</td>
 *       <td>Image File</td>
 *       <td>Image displayed at the login page.</td>
 *       <td>.png</td>
 *     </tr>
 *   </table>
 *   <p>
 *     Admin Users can either drag & drop the respective file or manually select it. Once a file is selected, the <strong>Save</strong> button gets enabled. Admins can also download the file asset by clicking on the download icon.
 *     <br>
 *     Clicking on the Save button calls the AMS-05 Create Signed URL, which on success calls the Upload GCP File PUT Call.
 *   </p>
 * </section>
 * <section>
 *   <h4>Failure Status Codes</h4>
 *   <p>
 *     This section describes the status codes for failures related to the Zendesk User Requests.
 *     <br>
 *     <table>
 *       <tr>
 *         <th>HTTP Status Code</th>
 *         <th>Service Error Code</th>
 *         <th>Error Message</th>
 *       </tr>
 *       <tr>
 *         <td>401</td>
 *         <td>UMS_AUTH_REQUIRED</td>
 *         <td>You must authenticate before you can use user-management-service.</td>
 *       </tr>
 *       <tr>
 *         <td>500</td>
 *         <td>NA</td>
 *         <td>Internal Server Error</td>
 *       </tr>
 *     </table>
 *   </p>
 * </section>
 * <section>
 *   <h5>Platform Service(s)</h5>
 *   <p>Table lists all the dependent platform service(s) with specific version(s) for the Create/View request page.</p>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Service Name</th>
 *       <th>Version</th>
 *     </tr>
 *     <tr>
 *       <td>Asset Management Service</td>
 *       <td>1.3.0</td>
 *     </tr>
 *     <tr>
 *       <td>User Management Service</td>
 *       <td>1.2.2</td>
 *     </tr>
 *   </table>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Feature</th>
 *       <th>API URL</th>
 *       <th>API Method</th>
 *       <th>API Permission</th>
 *       <th>Required</th>
 *     </tr>
 *     <tr>
 *       <td>Create Signed URL</td>
 *       <td>/signed-url</td>
 *       <td>POST</td>
 *       <td>asset-management-service.signed-url.create</td>
 *       <td>Yes</td>
 *     </tr>
 *   </table>
 * </section>
 */

const Assets = () => {
  const { t } = useTranslation();
  const { getRTLLayout } = useLocalization();
  const rtlClasses = getRTLLayout();
  const dispatch = useAppDispatch();

  const [preview, setPreview] = useState<any>(null);
  const [tempKey, setTempKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<any>({});
  const [file, setFile] = useState<any>(null);

  const userData = useAppSelector(
    (state: any) => state.userManagement.userProfileInfo
  );

  const initialLoadData: any = useAppSelector((state) => state.initialLoadData);

  const onDrop = useCallback(
    async (acceptedFiles, key) => {
      if (!acceptedFiles.length) {
        dispatch(
          setToastData({
            toastMessage: t("T_NOT_UPLOADED"),
            isToastActive: true,
            toastType: "error",
          })
        );
        return;
      }
      setFile({ ...file, [key]: acceptedFiles });

      dispatch(
        setToastData({
          toastMessage: t("T_FILE_ADDED"),
          isToastActive: true,
          toastType: "success",
        })
      );
    },
    [file]
  );

  const handleUpload = (key: string) => {
    inputRef.current.click();
    setTempKey(key);
  };

  useEffect(() => {
    if ((!file || isEmpty(file)) && !preview) {
      setPreview(null);
    } else {
      if (!isEmpty(file)) {
        Object.entries(file).map(([type, fileData]: any) => {
          const reader = new FileReader();
          reader.onload = (event: any) => {
            const dataUrl = event.target.result;
            setPreview({ ...preview, [type]: dataUrl });
          };
          reader.readAsDataURL(fileData[0]);
        });
      }
    }
  }, [file]);

  useEffect(() => {
    const { useLocalSplash, useLocalBanner, useLocalLogo } = initialLoadData;
    async function init() {
      try {
        const [response1, response2, response3] = await Promise.all([
          fetch(AppConstants.getLogoSplashUrl()),
          fetch(AppConstants.getLogoSolutionUrl()),
          fetch(AppConstants.getLogoIconUrl()),
        ]);

        const { status: status1 } = response1;
        const { status: status2 } = response2;
        const { status: status3 } = response3;

        if (HTTP_STATUS.isSuccess(status1) && useLocalSplash) {
          dispatch(setLocalAssets({ key: "useLocalSplash", flag: false }));
        }
        if (HTTP_STATUS.isSuccess(status2) && useLocalBanner) {
          dispatch(setLocalAssets({ key: "useLocalBanner", flag: false }));
        }
        if (HTTP_STATUS.isSuccess(status3) && useLocalLogo) {
          dispatch(setLocalAssets({ key: "useLocalLogo", flag: false }));
        }

        if (status3 === HTTP_STATUS.HTTP_OK) {
          setFavIcon();
        }
      } catch (e) {
        throw e;
      }
    }
    if (
      initialLoadData.useLocalSplash ||
      initialLoadData.useLocalBanner ||
      initialLoadData.useLocalLogo
    ) {
      init();
    }
  }, [
    initialLoadData.useLocalSplash,
    initialLoadData.useLocalBanner,
    initialLoadData.useLocalLogo,
  ]);

  const discardChanges = () => {
    if (inputRef?.current?.value) {
      inputRef.current.value = "";
    }
    setFile(null);
  };

  const saveChanges = async () => {
    function errorHandling(errorText: string) {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: errorText,
          isToastActive: true,
          toastType: "error",
        })
      );
    }
    setIsLoading(true);
    const uploadFilePasses: any = [];
    const uploadFileFailures: any = [];
    const fileUploadPromise = async (file: any, type: any) => {
      const { url, fileName } = type;
      const payload = {
        filePath: `${AppConstants.getGCPPath()}${fileName}`,
        bucketName: getGlobalBucketName(),
        versioning: true,
        action: AppConstants.GCS_UPLOAD_ACTION,
        publicObject: true,
        expirationTime: AppConstants.GCS_MAX_EXPIRATION_TIME,
        metadata: {
          fileName,
          key: getFileName(fileName),
          userId: userData ? userData.userId : "",
          [AppConstants.HEADER_CACHE_CONTROL]: AppConstants.VALUE_NO_CACHE,
          [AppConstants.HEADER_MAX_AGE]: AppConstants.VALUE_MAX_AGE_MINIMUM,
        },
      };
      const signedUrlResponse = await createSignedURL(payload);
      const { status, data } = signedUrlResponse;
      if (data) {
        const { url, headers = {}, issues = [] } = data;
        if (status == HTTP_STATUS.HTTP_CREATED) {
          let [fileObj] = file;
          const uploadPayload = {
            url,
            body: fileObj,
            headers: {
              ...headers,
              [AppConstants.HEADER_CONTENT_TYPE]: fileObj?.type,
            },
          };

          await uploadFile(uploadPayload);
          uploadFilePasses.push({ type });
          return {
            uploadFilePasses,
          };
        } else if (status == HTTP_STATUS.HTTP_BAD_REQUEST) {
          uploadFileFailures.push({ type, issues });
        }
      }
    };
    const forEachSeries = async (assets: any) => {
      const promises = Object.entries(assets).map(async ([type, file]: any) => {
        let assetObj = find(customizationOptions, { key: type });
        return fileUploadPromise(file, assetObj);
      });
      return Promise.all(promises);
    };
    try {
      const response: any = await forEachSeries(file);
      if (uploadFileFailures.length) {
        dispatch(
          setToastData({
            isToastActive: true,
            toastMessage: `${
              uploadFilePasses.length
                ? t("T_FILES_UPLOAD_PARTIAL_SUCCESS")
                : t("T_FILES_UPLOAD_ALL_FAILURE")
            }`
              .replace(
                "%s",
                `${
                  uploadFilePasses.length
                    ? uploadFilePasses.length
                    : uploadFileFailures.length
                }`
              )
              .replace(
                "%d",
                `${uploadFilePasses.length + uploadFileFailures.length}`
              ),
            toastType: uploadFilePasses.length ? "info" : "error",
          })
        );
        setIsLoading(false);
      } else if (uploadFilePasses.length) {
        setIsLoading(false);
        dispatch(
          setToastData({
            toastMessage: t("T_CUSTOMIZATION_UPDATE_SUCCESS"),
            isToastActive: true,
            toastType: "success",
          })
        );
        // setFile(null);
        if (inputRef?.current?.value) {
          inputRef.current.value = "";
        }
      }
    } catch (error: any) {
      errorHandling("Error");
    }
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <div className="p-4">
        <MenuButtonsPortal>
          <BiButton
            className={`tertiary ${rtlClasses.marginRight} border py-1.5`}
            type="button"
            onClick={discardChanges}
          >
            {t("T_CANCEL")}
          </BiButton>

          <BiButton
            className={`${rtlClasses.marginLeft} bg-primary text-white`}
            type="button"
            disabled={isEmpty(file)}
            onClick={saveChanges}
          >
            {t("T_SAVE")}
          </BiButton>
        </MenuButtonsPortal>
        <Card
          title={t("T_CUSTOMIZATION_ASSETS")}
          icon={swatch}
          alt="Clip Icon"
          height=""
          width=""
          className="w-3/4"
        >
          <div className="flex flex-col">
            {customizationOptions.map((asset: any, index: number) => {
              return (
                <CollapsibleCard
                  label={t(asset?.display)}
                  key={asset?.key}
                  index={index}
                >
                  <div className="flex flex-col items-center justify-center">
                    <div className="mt-5 text-base font-medium">
                      {t(asset?.description)}
                    </div>
                    {asset?.showPreview ? (
                      <div className="my-8 w-80">
                        <img
                          src={
                            file?.[asset?.key]
                              ? preview?.[asset.key]
                              : initialLoadData?.[asset?.localKey]
                              ? asset?.key === "splashImg"
                                ? splashImg
                                : bannerImg
                              : asset?.url
                          }
                          alt={t("T_CUSTOMIZATION_NO_PREVIEW")}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="my-4 flex h-20 w-full items-center justify-center border-dotted border-[#D9D9D9] bg-gray-100">
                        <p className="text-5xl font-bold text-[#CACACA]">
                          {t("T_CUSTOMIZATION_NO_PREVIEW")}
                        </p>
                      </div>
                    )}

                    <div className="mx-auto w-[75%] py-5">
                      <Dropzone
                        onDrop={(files: any) => onDrop(files, asset.key)}
                        accept={asset?.allowedTypes}
                        maxFiles={1}
                      />
                    </div>
                    <div className="flex flex-row items-center justify-center">
                      <IconButton
                        className={
                          "!m-0 h-10 w-10 border border-solid border-primary-disabled text-primary"
                        }
                        disabled={isEmpty(asset)}
                        onClick={() => {
                          window.open(asset?.url, "_blank");
                        }}
                      >
                        {downloadIcon}
                      </IconButton>

                      <BiButton
                        className={"ml-4 bg-primary text-white"}
                        type="button"
                        onClick={() => handleUpload(asset?.key)}
                        disabled={isEmpty(asset)}
                      >
                        {t("T_UPLOAD")} {t(asset?.display)}
                      </BiButton>
                      <input
                        type="file"
                        ref={inputRef}
                        data-testid={"customization-ref"}
                        onChange={(e) => {
                          onDrop(e.target.files, tempKey);
                        }}
                        style={{ display: "none" }}
                        accept={findAssetFormat(customizationOptions, tempKey)}
                      />
                    </div>
                  </div>
                </CollapsibleCard>
              );
            })}
          </div>
        </Card>
      </div>
    </LoaderContainer>
  );
};

export default Assets;
